import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

const SORT_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'spent', label: 'Total spent' },
  { value: 'points', label: 'Reward points' },
  { value: 'pending', label: 'Pending amount' },
  { value: 'referrals', label: 'Most referrals' },
];

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [customerPendingAmounts, setCustomerPendingAmounts] = useState({});
  const [totalCustomerCount, setTotalCustomerCount] = useState(0);
  const [listIsFiltered, setListIsFiltered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    idProof: '',
    notes: ''
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.API_URL}/customers`);
      setCustomers(response.data);
      setTotalCustomerCount(Array.isArray(response.data) ? response.data.length : 0);
      setListIsFiltered(false);
      
      // Fetch pending amounts for all customers
      await fetchPendingAmounts(response.data);
      
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch customers');
      setLoading(false);
      console.error(err);
    }
  };
  
  const fetchPendingAmounts = async (customersList) => {
    try {
      // Create a map to store customer pending amounts
      const pendingAmounts = {};
      
      // Get all active EMIs
      const emisResponse = await axios.get(`${config.API_URL}/emi?status=active`);
      const activeEmis = emisResponse.data;
      
      // Group EMIs by customer phone
      const emisByCustomer = {};
      activeEmis.forEach(emi => {
        const phone = emi.customer.phone;
        if (!emisByCustomer[phone]) {
          emisByCustomer[phone] = [];
        }
        emisByCustomer[phone].push(emi);
      });
      
      // Calculate pending amount for each customer
      customersList.forEach(customer => {
        const customerEmis = emisByCustomer[customer.phone] || [];
        const totalPending = customerEmis.reduce((total, emi) => {
          const totalPaid = emi.payments.reduce((sum, payment) => 
            payment.status === 'completed' ? sum + payment.amount : sum, 0);
          
          const pendingAmount = emi.emiPlan.totalAmount - totalPaid;
          return total + pendingAmount;
        }, 0);
        
        pendingAmounts[customer._id] = {
          amount: totalPending,
          count: customerEmis.length
        };
      });
      
      setCustomerPendingAmounts(pendingAmounts);
    } catch (err) {
      console.error('Error fetching pending amounts:', err);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchCustomers();
      return;
    }
    
    try {
      setLoading(true);
      // Use the new universal search endpoint
      const response = await axios.get(`${config.API_URL}/customers/search/${encodeURIComponent(searchTerm)}`);
      setCustomers(response.data);
      setListIsFiltered(true);
      setLoading(false);
    } catch (err) {
      setError('Failed to search customers');
      setLoading(false);
      console.error(err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer({ ...newCustomer, [name]: value });
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${config.API_URL}/customers`, newCustomer);
      setNewCustomer({
        name: '',
        phone: '',
        email: '',
        address: '',
        idProof: '',
        notes: ''
      });
      setShowAddForm(false);
      fetchCustomers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add customer');
      console.error(err);
    }
  };

  // Function to check if a customer is a witness
  const isWitness = (customer) => {
    return (
      customer.witnessProfile?.isWitness || 
      (customer.witnessedPurchases && customer.witnessedPurchases.length > 0) ||
      (customer.witnessProfile?.totalReferrals > 0)
    );
  };

  // Function to get witness referral count
  const getWitnessReferrals = (customer) => {
    if (customer.witnessProfile?.totalReferrals) {
      return customer.witnessProfile.totalReferrals;
    } else if (customer.witnessedPurchases) {
      return customer.witnessedPurchases.length;
    }
    return 0;
  };

  const displayedCustomers = useMemo(() => {
    if (sortBy === 'default') return customers;
    const list = [...customers];
    const pendingOf = (c) => Number(customerPendingAmounts[c._id]?.amount || 0);
    list.sort((a, b) => {
      if (sortBy === 'spent') return (Number(b.totalSpent) || 0) - (Number(a.totalSpent) || 0);
      if (sortBy === 'points') return (Number(b.rewardPoints) || 0) - (Number(a.rewardPoints) || 0);
      if (sortBy === 'pending') return pendingOf(b) - pendingOf(a);
      if (sortBy === 'referrals') return getWitnessReferrals(b) - getWitnessReferrals(a);
      return 0;
    });
    return list;
  }, [customers, sortBy, customerPendingAmounts]);

  const sortHeader = (key, label) => (
    <button
      type="button"
      className={`btn btn-link text-white text-decoration-none p-0 fw-semibold ${sortBy === key ? 'text-warning' : ''}`}
      onClick={() => setSortBy(key)}
      title={`Sort by ${label} (high → low)`}
    >
      {label}
      {sortBy === key ? ' ▼' : ''}
    </button>
  );

  return (
    <div className="customers-container">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <h2 className="mb-0">Customer Management</h2>
        {!loading && (
          <div className="text-md-end">
            <span className="badge bg-secondary fs-6 px-3 py-2">
              Total customers:{' '}
              <strong>{totalCustomerCount.toLocaleString('en-IN')}</strong>
              {listIsFiltered && (
                <span className="fw-normal">
                  {' '}
                  · Showing {customers.length.toLocaleString('en-IN')}
                </span>
              )}
            </span>
          </div>
        )}
      </div>
      
      <div className="row mb-4 g-2 align-items-end">
        <div className="col-lg-6">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Search by name or phone number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            />
            <button className="btn btn-primary" onClick={handleSearch}>Search</button>
            <button className="btn btn-secondary" onClick={fetchCustomers}>Clear</button>
          </div>
        </div>
        <div className="col-lg-3">
          <label className="form-label small mb-1">Sort (high → low)</label>
          <select
            className="form-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="col-lg-3 text-lg-end">
          <button 
            className="btn btn-success" 
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? 'Cancel' : 'Add New Customer'}
          </button>
        </div>
      </div>

      {showAddForm && (
        <div className="card mb-4">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">Add New Customer</h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleAddCustomer}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={newCustomer.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Phone *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="phone"
                    value={newCustomer.phone}
                    onChange={(e) => {
                      // Only allow digits and limit to 10 characters
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setNewCustomer({...newCustomer, phone: value});
                    }}
                    onKeyPress={(e) => {
                      // Prevent non-digit characters
                      if (!/\d/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    placeholder="Enter 10-digit mobile number"
                    required
                    maxLength="10"
                    pattern="[0-9]{10}"
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={newCustomer.email}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">ID Proof</label>
                  <input
                    type="text"
                    className="form-control"
                    name="idProof"
                    value={newCustomer.idProof}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Address</label>
                <textarea
                  className="form-control"
                  name="address"
                  value={newCustomer.address}
                  onChange={handleInputChange}
                  rows="2"
                ></textarea>
              </div>
              <div className="mb-3">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  name="notes"
                  value={newCustomer.notes}
                  onChange={handleInputChange}
                  rows="2"
                ></textarea>
              </div>
              <div className="text-end">
                <button type="submit" className="btn btn-success">Add Customer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}
      
      {loading ? (
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : customers.length === 0 ? (
        <div className="alert alert-info">No customers found</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead className="table-dark">
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Total Purchases</th>
                <th>{sortHeader('spent', 'Total Spent')}</th>
                <th>{sortHeader('points', 'Reward Points')}</th>
                <th>{sortHeader('pending', 'Pending Amount')}</th>
                <th>{sortHeader('referrals', 'Witness Status')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedCustomers.map(customer => {
                const pendingData = customerPendingAmounts[customer._id] || { amount: 0, count: 0 };
                return (
                  <tr key={customer._id}>
                    <td>{customer.name}</td>
                    <td>{customer.phone}</td>
                    <td>{customer.totalPurchases}</td>
                    <td>₹{customer.totalSpent?.toFixed(2)}</td>
                    <td>{customer.rewardPoints}</td>
                    <td>
                      {pendingData.amount > 0 ? (
                        <span className="text-danger">
                          ₹{pendingData.amount.toLocaleString()}
                          {pendingData.count > 0 && (
                            <span className="badge bg-warning text-dark ms-2">
                              {pendingData.count} EMI{pendingData.count !== 1 ? 's' : ''}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-success">₹0.00</span>
                      )}
                    </td>
                    <td>
                      {isWitness(customer) ? (
                        <div>
                          <span className="badge bg-success">Witness</span>
                          {getWitnessReferrals(customer) > 0 && (
                            <span className="badge bg-info ms-1">
                              {getWitnessReferrals(customer)} Referrals
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted">Not a witness</span>
                      )}
                    </td>
                    <td>
                      <Link to={`/customer/${customer._id}`} className="btn btn-sm btn-primary me-2">
                        View Details
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Customers; 